<template>
  <BreadCrumb class="hidden sm:block" />
  <ProductPage v-bind="{ entry, product, addOn }" />
</template>

<script setup>
import gql from 'graphql-tag';
import { computed } from 'vue';
import { stripLocale } from '@/utils/i18n';
import { SeomaticFragment, EntryPageFragment, ShopHomeBreadcrumbFragment } from '@/mixins/entryPage';
import BreadCrumb from '@/components/layout/BreadCrumb';
import ProductPage, { ProductPageFragment } from '@/components/shop/products/ProductPage';
import { productMetaFieldTransformer } from '@/components/shop/products/Product';
import { fetchAddOnProduct } from '@/utils/product';
import { useEntryPage } from '@/composables/useEntryPage';
import { checkContentProtection } from '@/utils/contentProtection';
import { useBlackrollAuth } from '@/composables/useBlackrollAuth';
import { useLoyalty } from '@/composables/useLoyalty';
import { useUserData } from '@/composables/useUserData';

const ProductPageQuery = gql`
  query ProductPageQuery($site: String!, $slug: String!, $uri: String!) {
    entry(section: "products", site: [$site], slug: [$slug]) {
      ...EntryPageFragment
      ...ProductPageFragment
    }
    ...ShopHomeBreadcrumbFragment

    seomatic(site: $site, uri: $uri, asArray: true) {
      ...SeomaticFragment
    }
  }
  ${EntryPageFragment}
  ${ProductPageFragment}
  ${SeomaticFragment}
  ${ShopHomeBreadcrumbFragment}
`;

// All composables must be called in setup scope, not inside useAsyncData callback
const nuxtApp = useNuxtApp();
const { $shopifyProductByHandle, $craftRequest, $i18n, $localeRootPath } = nuxtApp;
const auth = useBlackrollAuth();
const loyalty = useLoyalty();
const userData = useUserData();
const { locale } = $i18n;
const route = useRoute();

const { data } = await useAsyncData(`product-${route.params.slug}`, async () => {
  const uri = stripLocale(route.path, locale.value);
  const { slug } = route.params;
  const variables = { site: locale.value, slug, uri };

  // fetch craft cms data
  const entryRequest = await $craftRequest(ProductPageQuery, variables);
  const { entry, seomatic, shopHomeEntry } = entryRequest;

  // Check content protection
  await checkContentProtection(entry, nuxtApp, auth, loyalty, userData);

  // extract shopify product handle for shopify data request
  const handle = entry?.shopifyHandle ?? route.params.slug;
  const product = await $shopifyProductByHandle(handle);

  if (!product) {
    throw createError({ statusCode: 404, statusMessage: 'Product Not Found', fatal: true });
  }

  const transformedProduct = productMetaFieldTransformer(product);
  const addOnProduct = await fetchAddOnProduct(transformedProduct, $shopifyProductByHandle);

  // Build breadcrumbs as data (no composable calls here)
  const breadcrumbCollection = entry?.breadcrumbCollection?.[0] || null;
  const breadCrumb = [];

  if (shopHomeEntry) {
    breadCrumb.push({
      label: shopHomeEntry.title,
      route: $localeRootPath(shopHomeEntry.uri),
    });
  }

  if (entry) {
    if (breadcrumbCollection) {
      breadCrumb.push({
        label: breadcrumbCollection.title,
        route: $localeRootPath(breadcrumbCollection.uri),
      });
    }
    breadCrumb.push({
      label: entry.title,
      route: $localeRootPath(entry.uri),
    });
  }

  return {
    product: transformedProduct,
    addOn: addOnProduct,
    entry,
    seomatic,
    shopHomeEntry,
    breadcrumbCollection,
    breadCrumb,
  };
});

// These run in setup scope after useAsyncData resolves — safe to use composables
const entry = computed(() => data.value?.entry);
const product = computed(() => data.value?.product);
const addOn = computed(() => data.value?.addOn);
const breadcrumbCollection = computed(() => data.value?.breadcrumbCollection);
const breadcrumbItems = computed(() => data.value?.breadCrumb || []);

// SEO, meta, GTM
const firstProductImage = computed(() => {
  try {
    return product.value?.images?.edges[0]?.node?.src;
  } catch {
    return null;
  }
});

const { setBreadCrumb } = useEntryPage(entry.value, data.value?.seomatic, {
  content_category: 'shop',
  product_category: breadcrumbCollection.value?.collectionHandle,
  image_url: firstProductImage.value,
});

if (breadcrumbItems.value.length > 0) {
  setBreadCrumb(breadcrumbItems.value);
}
</script>
